import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, BookOpen } from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

export const AdminLedger = ({ client, api }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`${api}/qv2/customers`);
        setCustomers(data);
      } catch {}
    })();
  }, [client, api]);

  const load = async () => {
    if (!customerId) return toast.error("Pick a customer");
    setBusy(true);
    try {
      const { data } = await client.get(`${api}/qv2/ledger/${customerId}`);
      setData(data);
    } catch {
      toast.error("Could not load ledger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ledger-panel">
      <div className="bg-steel border border-white/10 p-5 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke block mb-2">Customer</label>
          <select data-testid="ledger-customer-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none min-w-[240px]">
            <option value="">Select…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
          </select>
        </div>
        <button data-testid="ledger-load-button" onClick={load} disabled={busy} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Load Ledger"}
        </button>
      </div>

      {!data ? (
        <div data-testid="ledger-empty" className="bg-steel border border-white/10 p-14 text-center">
          <BookOpen size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">Pick a customer</p>
        </div>
      ) : (
        <div data-testid="ledger-result">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Quotations</p>
              <p data-testid="ledger-quotations-count" className="font-heading font-semibold text-2xl text-white mt-1">{data.totals.quotations}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Confirmed</p>
              <p data-testid="ledger-confirmed" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(data.totals.confirmed)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Bills Raised</p>
              <p data-testid="ledger-bills-raised" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(data.totals.bills_raised)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Paid</p>
              <p data-testid="ledger-paid" className="font-heading font-semibold text-2xl text-green-300 mt-1">{fmt(data.totals.paid)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Outstanding</p>
              <p data-testid="ledger-outstanding" className="font-heading font-semibold text-2xl text-red-300 mt-1">{fmt(data.totals.outstanding)}</p>
            </div>
          </div>

          <div className="border border-white/10 bg-steel overflow-x-auto mb-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke px-4 pt-4">Quotations ({data.quotations.length})</p>
            <table data-testid="ledger-quotations-table" className="w-full min-w-[720px] mt-2">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                  <th className="py-2 px-4 font-medium">Quote #</th>
                  <th className="py-2 px-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Status</th>
                  <th className="py-2 px-4 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.quotations.length === 0 ? (
                  <tr><td colSpan={4} className="py-4 px-4 font-body text-sm text-smoke">No quotations for this customer yet.</td></tr>
                ) : data.quotations.map((q) => (
                  <tr key={q.id} className="border-t border-white/10">
                    <td className="py-3 px-4 font-mono text-xs text-[#7FB3E8]">{q.quote_no}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{fmtDate(q.created_at)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke uppercase">{q.status}</td>
                    <td className="py-3 px-4 font-mono text-xs text-white text-right">{fmt(q.grand_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-white/10 bg-steel overflow-x-auto">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke px-4 pt-4">Sale Bills ({data.bills.length})</p>
            <table data-testid="ledger-bills-table" className="w-full min-w-[800px] mt-2">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                  <th className="py-2 px-4 font-medium">Bill #</th>
                  <th className="py-2 px-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Due Date</th>
                  <th className="py-2 px-4 font-medium text-right">Total</th>
                  <th className="py-2 px-4 font-medium text-right">Paid</th>
                  <th className="py-2 px-4 font-medium text-right">Due</th>
                </tr>
              </thead>
              <tbody>
                {data.bills.length === 0 ? (
                  <tr><td colSpan={6} className="py-4 px-4 font-body text-sm text-smoke">No bills for this customer yet.</td></tr>
                ) : data.bills.map((b) => (
                  <tr key={b.id} className="border-t border-white/10">
                    <td className="py-3 px-4 font-mono text-xs text-[#7FB3E8]">{b.bill_no}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{b.bill_date}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{b.due_date || "—"}</td>
                    <td className="py-3 px-4 font-mono text-xs text-white text-right">{fmt(b.total)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-green-300 text-right">{fmt(b.paid)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-red-300 text-right">{fmt(b.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
